import React, { useCallback, useEffect,useState } from "react";
import { AppButton, Box, FlexControlledForm, Inscription, useDebounceState } from '@common-fe/common-fe';

import { ModalWrapper } from '@/components/wrappers';
import { CheckDetails } from '@/modules/employer/components/Lockbox/lockbox.types';
import useCheckAddressFields, { FormValues } from '@/modules/employer/components/Lockbox/LockboxDetails/hooks/useCheckAddressFields';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useValidateForm } from '@/utils/hooks';

interface Props {
  checkDetails?: CheckDetails;
  onSetVisible: (value: boolean) => void;
}

const EditCheckAddressModal: React.FC<Props> = ({ checkDetails, onSetVisible }) => {
  const [formValues, setFormValues] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const { editableLockbox, setEditableLockbox } = useLockboxDetailsStore();
  const { addressFields } = useCheckAddressFields({ checkDetails });
  const { handlePreSubmit, hasErrors } = useValidateForm(addressFields);

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleUpdateAddress = useCallback(() => {
    const isSuccessValidation = handlePreSubmit(formValues);

    if (isSuccessValidation) {
      setEditableLockbox({
        remainder: {
          ...editableLockbox?.remainder,
          payTo: formValues?.vendorName,
          addressLine1: formValues?.line1,
          addressLine2: formValues?.line2,
          city: formValues?.city,
          state: formValues?.state,
          zip: formValues?.zipCode,
          undeliverable: false,
        }
      });
      handleClose();
    }
  }, [formValues, setEditableLockbox, handlePreSubmit, editableLockbox?.remainder, handleClose]);

  useEffect(() => {
    if (values) {
      setFormValues(values);
    }
  }, [values]);

  return (
    <ModalWrapper
      visible
      onSetVisible={onSetVisible}
      title="Edit Check Address"
      titleBoxStyle={{ paddingBottom: '12px' }}
      testId="edit-check-address"
    >
      <Box align="center">
        <Inscription data-testId="edit-check-address_description">
          We will send declined documents to this address
        </Inscription>
      </Box>

      <Box
        margin={{ top: 'spacing24' }}
        background="module"
        pad="spacing24"
        round="moduleRound"
      >
        <Box
          elevation='default'
          background="canvas"
          round="container1Round"
        >
          <FlexControlledForm
            fields={addressFields}
            onChangeValues={setValues}
            showError={hasErrors}
            isThinMode
            isModalType
            editMode
          />
        </Box>
      </Box>

      <Box data-testId="edit-check-address-modal_footer" margin={{ top: 'spacing24' }} direction="row" justify="end">
        <Box>
          <AppButton
            testId="edit-check-address-modal_cancel"
            onClick={handleClose}
            width="160px"
            buttonType="secondary"
          >
            Cancel
          </AppButton>
        </Box>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            testId="edit-check-address-modal_save"
            onClick={handleUpdateAddress}
            width="160px"
          >
            Save
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default EditCheckAddressModal;
